import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { AuthenticatedUserFragment } from '#src/fragments/AuthenticatedUser.generated'
import { useRegisterCustomerMutation } from '#src/components/OAuthRegister/RegisterCustomer.generated'
import { RegistrationHandle } from '#src/components/AuthenticationFlow/index'
import { TextField, PhoneField, EmailField, Button, Notification, Box } from '@deal/components'
import AuthenticationFlowViewLayout from '#src/components/AuthenticationFlow/components/AuthenticationFlowViewLayout'
import OAuthButtons from '#src/components/AuthenticationFlow/components/OAuthButtons'
import { RegisterViewProps } from '../../'
import { toast } from 'react-toastify'
import classnames from 'classnames'
import styles from './styles.css'

const RegistrationFrom: React.FC<RegisterViewProps> = ({
  onRegister,
  standalone,
  headline = 'Shop with an expert. Love what you buy.',
  subHeadline = 'Join Curated now - it’s free!',
  registrationHandle = RegistrationHandle.PHONE_AND_EMAIL,
  referralCodeId,
  submitDisabled,
  registrationId,
  className,
  footer
}) => {
  const [registerCustomer, registerCustomerResult] = useRegisterCustomerMutation()
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')

  const handleSubmit: React.FormEventHandler = e => {
    e.preventDefault()

    registerCustomer({
      variables: {
        input: {
          firstName: firstName,
          email: email,
          phone: phone,
          referralCodeId,
          registrationId
        }
      }
    }).then(result => {
      if (result.data?.registerCustomer.user) {
        const errorMessage = result.data.registerCustomer.claimPromotionResult?.errorMessage || ''
        onRegister(result.data.registerCustomer.user, errorMessage)
      }
    })
  }

  const registrationErrors = registerCustomerResult.data?.registerCustomer.errors

  let isSubmittable = !submitDisabled && !registerCustomerResult.loading && firstName.length > 0
  switch (registrationHandle) {
    case RegistrationHandle.EMAIL:
      isSubmittable = isSubmittable && email.length > 0
      break

    case RegistrationHandle.PHONE:
      isSubmittable = isSubmittable && phone.length > 0
      break

    case RegistrationHandle.PHONE_AND_EMAIL:
      isSubmittable = isSubmittable && email.length > 0 && phone.length > 0
      break
  }

  const showEmail =
    registrationHandle === RegistrationHandle.EMAIL ||
    registrationHandle === RegistrationHandle.PHONE_AND_EMAIL
  const showPhone =
    registrationHandle === RegistrationHandle.PHONE ||
    registrationHandle === RegistrationHandle.PHONE_AND_EMAIL

  return (
    <AuthenticationFlowViewLayout
      headline={headline}
      subHeadline={subHeadline}
      standalone={standalone}
      className={classnames(className)}
      footer={
        footer || (
          <p className={styles.tcpa}>
            By continuing, you agree to our <Link to="/legal/terms-services">Terms of Service</Link>{' '}
            and <Link to="/legal/privacy">Privacy Policy</Link>, and to receive occasional texts
            from us. Message and data rates may apply. Text STOP to opt out.
          </p>
        )
      }
    >
      <form onSubmit={handleSubmit}>
        {registrationErrors && (
          <Notification type="error" className={styles.errors}>
            {registrationErrors.join(', ')}
          </Notification>
        )}

        <Box className={styles.fields}>
          <Box.Item className={styles.fieldWrapper}>
            <TextField
              name="firstName"
              label="First name"
              labelHidden
              placeholder="First name*"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              className={styles.field}
              testId="register-first-name"
            />
          </Box.Item>
          {showEmail && (
            <Box.Item className={styles.fieldWrapper}>
              <EmailField
                name="email"
                label="Email"
                labelHidden
                type="email"
                placeholder="Email*"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className={styles.field}
                testId="register-email"
              />
            </Box.Item>
          )}
          {showPhone && (
            <Box.Item className={styles.fieldWrapper}>
              <PhoneField
                name="phone"
                label="Phone number"
                labelHidden
                type="tel"
                placeholder="Phone number*"
                helpText="We'll text order updates to this number"
                onChange={e => setPhone(e.target.value)}
                className={styles.field}
              />
            </Box.Item>
          )}
          <Box.Item className={styles.fakeFieldWrapper} />
        </Box>

        <Button
          variant="primary"
          className={styles.submitButton}
          disabled={!isSubmittable}
          type="submit"
          testId="register-submit"
        >
          Continue →
        </Button>

        <div className={styles.or}>
          <span>OR</span>
        </div>

        <OAuthButtons
          onFailure={toast.error}
          onSuccess={(user: AuthenticatedUserFragment) => onRegister(user)}
          referralCodeId={referralCodeId}
          registrationId={registrationId}
        />
      </form>
    </AuthenticationFlowViewLayout>
  )
}

export default RegistrationFrom
